'use client';
// Stock & logistique — les trois registres nés de la reprise documentaire du
// 06/08/2026 : lieux de stockage, prêts de produits, distribution de tubes.
//
// ── Pourquoi un registre et pas un document ────────────────────────────────
// Les trois pièces d'origine (ORG.K2.01, ENR.K2.05, ENR.K2.10) sont des
// TABLEAUX, pas des textes. Les verser en GED aurait donné deux sources de
// vérité qui divergent à la première mise à jour : le classeur qu'on modifie
// et le document figé qu'on oublie. C'est le défaut déjà corrigé plusieurs
// fois sur cette plateforme, et la propriétaire a tranché — registre.
//
// ── Depuis le 10/08/2026 : ces registres se TIENNENT ici ───────────────────
// La reprise a fait de la base la source ; un registre qu'on ne peut corriger
// que par script n'est pas tenu, il est subi. Le RQ crée, corrige et ARCHIVE
// (jamais de suppression — décision du 10/08/2026) ; un prêt ou une
// distribution se déclarent par la personne qui les fait. Chaque écriture
// passe par le moteur (`ged/stock/*`), qui journalise la différence sous le
// code du classeur d'origine.
//
// ── Ce que cet écran refuse de faire ──────────────────────────────────────
//
//  · ADDITIONNER DES PORTOIRS ET DES CARTONS. Le classeur tient deux tables
//    accolées avec deux unités ; un total unique n'aurait aucun sens physique.
//    Les deux natures se comptent séparément, toujours.
//
//  · FAIRE PASSER UN PRÊT SANS RETOUR POUR UN PRÊT EN COURS. Une ligne dont la
//    colonne « rendu » est vide n'est pas « en attente » : c'est une ligne dont
//    personne n'a tranché le sort. L'écran la compte à part et la nomme.
//
//  · CONFONDRE PRÊTER ET EMPRUNTER. Prêter engage un bien ; emprunter engage un
//    RÉSULTAT PATIENT, produit avec un réactif dont le laboratoire ne maîtrise
//    ni la conservation ni parfois le lot. Les deux sens ne se mélangent pas.
//
//  · LAISSER CROIRE QUE CE REGISTRE COMPTE LE STOCK. Il dit OÙ une chose est
//    rangée, pas COMBIEN il y en a — les quantités vivent dans le registre des
//    quantités (Matériel › Stock, `StockQuantites.tsx`, depuis le 20/10/2026).
import React, { useEffect, useMemo, useState } from 'react';
import { BoutonJournal } from '@/components/BoutonJournal';
import {
  I, card, TitrePage, EmptyState, FilterBar, RefISO, Skeleton, useBusy,
  Modale, inputStyle, Pastille } from '@/components/ui';
import {
  ActionsLigne, ArchiverModale, BadgeArchive, FormulaireLigne, useDemandeCreation,
} from '@/components/RegistreEdition';
import type { ChampDef } from '@/components/RegistreEdition';
import { Tableau } from '@/shared/Tableau';
import { useGed, userName } from '@/lib/store';
import { MissionsInventaire } from './MissionsInventaire';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { aujourdhuiIso, fmtDate } from '@/lib/meta';
type LigneBase = {
  documentId: string;
  archive: boolean | null; archiveMotif: string | null;
  modifieLe: string | null; modifiePar: string | null;
};
type Pret = LigneBase & {
  sens: string; date: string | null; personnel: string | null;
  article: string; quantite: string | null; demandeVia: string | null;
  canal: string | null; demandePar: string | null;
  datePret: string | null; saisieBoxylab: string | null; laboratoire: string | null;
  rendu: string | null; dateRetour: string | null; recuPar: string | null;
  facture: string | null; paye: string | null; remarque: string | null;
};
type Distribution = LigneBase & {
  nature: string; date: string | null;
  produitRef: string | null; produitLibelle: string | null; quantite: string | null;
  personnel: string | null; etablissement: string | null;
  /* ⚠️ LE NOM ÉCRIT AU CLASSEUR ET LA PERSONNE SONT DEUX CHOSES (28/08/2026).
     `personnel` est ce qui était noté à la main — « ASMA », « chaima » ; c'est
     la source, elle ne se réécrit pas. `personnelCle` est le rapprochement avec
     le registre des personnes. Une clé vide veut dire « pas rapprochée »,
     JAMAIS « personne » : la ligne a bien un auteur, écrit noir sur blanc. */
  personnelCle: string | null;
  etablissementLibelle: string | null; codeBoxystock: string | null; remarque: string | null;
};

/* `backend/config/api.ts` plafonne à 100 lignes SANS erreur ni avertissement :
 * une lecture non paginée afficherait 100 prêts sur 67… ou 100 sur 154 le jour
 * où le registre grossira, sans que rien ne le signale. On pagine. */
const PAGE = 100;
async function tout<T>(collection: string, tri: string): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; page <= 100; page += 1) {
    const r = await fetch(
      `/api/ged/${collection}?sort=${tri}&pagination[page]=${page}&pagination[pageSize]=${PAGE}`,
      { credentials: 'include' },
    ).catch(() => null);
    if (!r || !r.ok) break;
    const j = await r.json().catch(() => null);
    const data: T[] = j?.data ?? [];
    out.push(...data);
    if (!data.length || page >= (j?.meta?.pagination?.pageCount ?? 1)) break;
  }
  return out;
}

const tdS: React.CSSProperties = { padding: '9px 12px', fontSize: 13, color: 'var(--text-body)', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top' };
const thS: React.CSSProperties = { padding: '9px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', textAlign: 'left', borderBottom: '1px solid var(--border-default)', whiteSpace: 'nowrap' };
const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 12.5 };

/** Une valeur absente s'affiche comme absente — jamais comme une chaîne vide
 *  qu'on lirait « rien à signaler ». */
const V = ({ v }: { v: string | null | undefined }) => (
  v && v.trim()
    ? <>{v}</>
    : <span style={{ color: 'var(--text-muted)' }} title="Non renseigné dans la source">—</span>
);

/** `oui` / `non` du classeur, tel quel — on ne traduit pas les mots du labo. */
const estOui = (v: string | null | undefined) => /^oui$/i.test((v || '').trim());
const estNon = (v: string | null | undefined) => /^non$/i.test((v || '').trim());

function Cadre({ children, note }: { children: React.ReactNode; note?: React.ReactNode }) {
  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      {note && (
        <div style={{ padding: '10px 14px', fontSize: 12.5, color: 'var(--text-muted)', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-sunken)' }}>
          {note}
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>{children}</div>
    </div>
  );
}

/* ── Lieux de stockage : partis au PLAN DU LABORATOIRE (13/10/2026) ───────
   Le registre ORG.K2.01 se tient désormais sur l'étagère même, dans
   « Paramétrage › Plan du laboratoire & stockage » (`PlanLabo.tsx`) : le
   tableau à plat faisait taper zone, armoire et étagère en texte libre, et une
   ligne créée ici n'était rattachée à aucun endroit du plan. */

/* ── Prêts de produits ────────────────────────────────────────────────────── */

/** Les cinq façons dont une demande de prêt arrive. Liste OUVERTE (datalist) :
 *  un canal inattendu doit pouvoir s'écrire plutôt que d'obliger à choisir
 *  faux — le registre reprend les mots du laboratoire, pas l'inverse. */
const CANAUX = ['Téléphone', 'WhatsApp', 'SMS', 'Courriel', 'Sur place'] as const;

/** Oui / Non / non tranché. Le vide est un ÉTAT, pas une absence de saisie :
 *  sur ce registre, 31 lignes n'ont jamais tranché « facturé » et le dire vaut
 *  mieux que de les compter comme non facturées. */
const TRI_ETAT = [['', 'Non tranché'], ['OUI', 'Oui'], ['NON', 'Non']] as const;

/** Les champs du prêt, construits à l'exécution : les personnes viennent d'un référentiel vivant ; le partenaire
 *  (laboratoire ou fournisseur) se choisit dans l'ANNUAIRE (type `contact`, 20/10/2026).
 *
 *  Deux natures de liste, choisies exprès :
 *   · `options` (liste FERMÉE) pour les personnes et les états — l'ensemble
 *     est connu et court ;
 *   · `suggestions` (liste CHERCHABLE, valeur libre) pour le laboratoire —
 *     120 entrées entre confrères et fournisseurs, et surtout des valeurs
 *     historiques qu'une liste fermée invaliderait sans rien résoudre.
 *  Dans les deux cas, une valeur hors liste est CONSERVÉE (cf. `Champ`). */
function champsPret(personnes: string[]): readonly ChampDef[] {
  const gens = [['', '—'] as const, ...personnes.map((n) => [n, n] as const)];
  return [
    { k: 'sens', label: 'Sens', type: 'options', options: [['sortant', 'Prêté par le laboratoire'], ['entrant', 'Emprunté à un autre laboratoire']] },
    { k: 'date', label: 'Date' },
    { k: 'article', label: 'Article', requis: true },
    { k: 'quantite', label: 'Quantité' },
    /* Le partenaire se CHOISIT dans l'annuaire (Ines, 20/10/2026 : « il doit lire à partir de nos correspondants ») ;
       son nom est recopié sur le prêt par le moteur, pour l'historique. */
    { k: 'partenaireCode', label: 'Laboratoire ou fournisseur', type: 'contact', familles: ['laboratoires', 'fournisseurs', 'cliniques'], requis: true },
    { k: 'personnel', label: 'Personnel BIO24 qui a traité', type: 'options', options: gens },
    { k: 'canal', label: 'Demande reçue par', suggestions: CANAUX },
    { k: 'demandePar', label: 'Demande faite par', type: 'options', options: gens },
    { k: 'datePret', label: 'Date du prêt' },
    { k: 'saisieBoxylab', label: 'Saisie Boxylab', type: 'options', options: TRI_ETAT },
    { k: 'rendu', label: 'Rendu', type: 'options', options: [['', 'Non tranché'], ['oui', 'Oui'], ['non', 'Non']] },
    { k: 'dateRetour', label: 'Date de retour' },
    { k: 'recuPar', label: 'Reçu par', type: 'options', options: gens },
    { k: 'facture', label: 'Facturé', type: 'options', options: TRI_ETAT },
    { k: 'paye', label: 'Payé', type: 'options', options: TRI_ETAT },
    { k: 'remarque', label: 'Remarque', type: 'long' },
    /* Ancienne colonne, en LECTURE de fait : elle n'est plus proposée à la
       saisie mais reste corrigeable, sans quoi les 50 lignes reprises
       porteraient à l'écran une valeur qu'aucun formulaire ne peut amender. */
    { k: 'demandeVia', label: 'Demande via (ancienne colonne, reprise Excel)', large: true },
  ];
}

/** Pastille de statut du prêt : AMBRE tant que l'objet est dehors, VERT quand
 *  il est revenu — le même code couleur que le reste de la plateforme
 *  (`--status-warning` / `--status-success`). Le rouge reste réservé au
 *  « non rendu » tranché : ce n'est pas une attente, c'est une perte actée. */
/* L9 : sur la Pastille du socle (É-12) — la logique (rendu / non rendu / dehors) reste ici. */
function BadgeRetour({ p }: { p: Pret }) {
  if (estOui(p.rendu)) return <Pastille ton="success"><I n="check" s={12} />Rendu{p.dateRetour ? ` · ${fmtDate(p.dateRetour)}` : ''}</Pastille>;
  if (estNon(p.rendu)) return <Pastille ton="danger">Non rendu</Pastille>;
  return (
    <Pastille ton="warning" title="L’objet est dehors : personne n’a encore acté son retour — ni dans un sens ni dans l’autre.">
      {p.sens === 'entrant' ? 'Emprunté — dehors' : 'Prêté — dehors'}
    </Pastille>
  );
}

/** Acter le retour d'un prêt — la date se CONFIRME, elle ne se suppose pas.
 *
 *  Le jour même est proposé parce que c'est le cas fréquent, jamais parce
 *  qu'il serait certain : un retour se constate souvent le lendemain, ou le
 *  lundi pour le vendredi. La date reste donc un champ, pas une valeur cachée.
 *  Le commentaire est facultatif et s'ajoute à la remarque de la ligne. */
function RetourModale({ p, onClose, onValider, occupe }: {
  p: Pret; onClose: () => void;
  onValider: (date: string, commentaire: string) => void;
  occupe: boolean;
}) {
  const aujourdhui = aujourdhuiIso();
  const [date, setDate] = useState(p.dateRetour || aujourdhui);
  const [commentaire, setCommentaire] = useState('');
  const sorti = p.datePret || p.date || '';
  // Un retour ANTÉRIEUR au prêt est une faute de saisie, pas un cas limite :
  // on le dit avant l'enregistrement, là où il se corrige encore.
  const incoherent = !!sorti && !!date && date < sorti;

  return (
    <Modale
      titre={`Acter le retour — ${p.article}`}
      sousTitre={`${p.sens === 'entrant' ? 'Emprunté à' : 'Prêté à'} ${p.laboratoire || '—'}${sorti ? ` · sorti le ${sorti}` : ''}`}
      onClose={onClose}
      pied={(
        <div style={{ display: 'flex', gap: 10, width: '100%', alignItems: 'center' }}>
          <span style={{ flex: 1 }} />
          <Bouton taille="sm" onClick={onClose}>Annuler</Bouton>
          <Bouton variante="primaire" taille="sm" onClick={() => onValider(date, commentaire)} disabled={occupe || !date || incoherent}>
            <I n="check" s={13} />{occupe ? 'Enregistrement…' : 'Acter le retour'}
          </Bouton>
        </div>
      )}>
      <div style={{ display: 'grid', gap: 12 }}>
        <label style={{ display: 'grid', gap: 5, fontSize: 12.5, fontWeight: 600, color: 'var(--text-body)' }}>
          <span>Date du retour <span style={{ color: 'var(--red-600)' }}>*</span></span>
          <input type="date" value={date} max={aujourdhui} onChange={(e) => setDate(e.target.value)}
            style={{ ...inputStyle, fontWeight: 400 }} />
          <span style={{ fontWeight: 400, fontSize: 12.5, color: 'var(--text-muted)' }}>
            Le jour même est proposé — corrigez-le si le retour date d’avant.
          </span>
        </label>
        {incoherent && (
          <p role="alert" style={{ margin: 0, fontSize: 12.5, color: 'var(--red-600)', lineHeight: 1.45 }}>
            Ce retour serait antérieur à la sortie du {sorti}.
          </p>
        )}
        <label style={{ display: 'grid', gap: 5, fontSize: 12.5, fontWeight: 600, color: 'var(--text-body)' }}>
          <span>Commentaire <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>· facultatif</span></span>
          <textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)} rows={3}
            placeholder="Rendu incomplet, flacon entamé, lot différent…"
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', fontWeight: 400, resize: 'vertical', fontFamily: 'var(--font-sans)' }} />
        </label>
      </div>
    </Modale>
  );
}

function Prets({ isRQ, signalCreation }: { isRQ: boolean; signalCreation: number }) {
  const [lignes, setLignes] = useState<Pret[] | null>(null);
  const [version, setVersion] = useState(0);
  const [q, setQ] = useState('');
  const [f, setF] = useState('tous');
  const [edition, setEdition] = useState<Pret | 'nouveau' | null>(null);
  const [archivage, setArchivage] = useState<Pret | null>(null);
  const { smq, settings, session } = useGed();
  const { run, estOccupe } = useBusy();
  const [retour, setRetour] = useState<Pret | null>(null);
  useDemandeCreation(signalCreation, () => setEdition('nouveau'));

  /* Les personnes proposées viennent du paramétrage (`gedUsers`) et non du
     module RH : cet écran est ouvert à tout le personnel, qui n'a pas le droit
     de lire l'effectif. Un compte suspendu reste proposé s'il figure déjà sur
     une ligne ancienne — c'est un registre, il décrit le passé. */
  const personnes = useMemo(
    () => (settings?.gedUsers || []).map((u) => u.nom).filter(Boolean).sort((a, b) => a.localeCompare(b, 'fr')),
    [settings?.gedUsers],
  );
  const PRETS_CHAMPS = useMemo(() => champsPret(personnes), [personnes]);

  /* « Marquer rendu » — le geste le plus fréquent du registre.
   *
   * Il actait le retour À LA DATE DU JOUR, sans rien demander. C'était faux
   * une fois sur deux : un retour se constate souvent le lendemain, ou le
   * lundi pour le vendredi. La date du jour est désormais PROPOSÉE et non
   * imposée — celui qui acte la corrige d'un geste — et un commentaire
   * facultatif recueille ce que la seule date ne dit pas (« rendu incomplet »,
   * « flacon entamé »). Les deux partent au journal ENR.K2.05. */
  const acterRetour = (p: Pret, date: string, commentaire: string) => run(`rendu-${p.documentId}`, async () => {
    const ok = await smq(`ged/stock/prets/${p.documentId}`,
      {
        rendu: 'oui',
        dateRetour: date,
        // Le commentaire s'AJOUTE à la remarque existante au lieu de l'écraser :
        // elle porte souvent la raison du prêt, qui reste vraie après le retour.
        ...(commentaire.trim()
          ? { remarque: [p.remarque, `Retour : ${commentaire.trim()}`].filter(Boolean).join(' — ') }
          : {}),
      },
      `Retour acté au ${date} — la différence est au journal.`);
    if (ok) { setRetour(null); setVersion((n) => n + 1); }
  });

  useEffect(() => {
    let vivant = true;
    void tout<Pret>('ged-pret-produits', 'date:desc')
      .then((d) => { if (vivant) setLignes(d); });
    return () => { vivant = false; };
  }, [version]);

  const l = useMemo(() => (lignes || []).filter((p) => !p.archive), [lignes]);
  const archivees = useMemo(() => (lignes || []).filter((p) => p.archive), [lignes]);
  const sortants = l.filter((p) => p.sens === 'sortant');
  const entrants = l.filter((p) => p.sens === 'entrant');
  // « Sans retour tranché » n'est PAS « en cours » : c'est une ligne que
  // personne n'a close, ni dans un sens ni dans l'autre.
  const sansRetour = l.filter((p) => !estOui(p.rendu) && !estNon(p.rendu) && !(p.dateRetour || '').trim());
  const nonRendus = l.filter((p) => estNon(p.rendu));

  const vues = useMemo(() => {
    const t = q.trim().toLowerCase();
    const base = f === 'sortants' ? sortants : f === 'entrants' ? entrants
      : f === 'sansretour' ? sansRetour : f === 'nonrendus' ? nonRendus
        : f === 'archives' ? archivees : l;
    return base.filter((p) => !t
      || [p.article, p.laboratoire, p.personnel].some((v) => (v || '').toLowerCase().includes(t)));
  }, [lignes, q, f]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!lignes) return <Skeleton rows={6} />;
  if (!lignes.length && edition === null) {
    return (
      <EmptyState
        titre="Aucun prêt de produit n’est enregistré."
        indice="Un registre vide ne veut pas dire qu’aucun réactif ne circule entre laboratoires : il veut dire que rien n’en est tracé." />
    );
  }

  return (
    <div>
      <FilterBar
        q={q} onQ={setQ} placeholder="Chercher un article, un laboratoire…"
        chips={[
          { k: 'tous', label: 'Tous', n: l.length },
          { k: 'sortants', label: 'Prêtés par le laboratoire', n: sortants.length },
          { k: 'entrants', label: 'Empruntés à un autre laboratoire', n: entrants.length },
          { k: 'nonrendus', label: 'Non rendus', n: nonRendus.length },
          { k: 'sansretour', label: 'Retour non tranché', n: sansRetour.length },
          ...(archivees.length ? [{ k: 'archives', label: 'Archivés', n: archivees.length }] : []),
        ]}
        actif={f} onChip={setF}
        total={l.length} affiches={vues.length}
        onReset={() => { setQ(''); setF('tous'); }} />
      <Cadre note={<>
        <strong>Emprunter</strong> n’est pas le symétrique de <strong>prêter</strong> : un réactif emprunté sert à rendre
        un résultat patient alors que le laboratoire ne maîtrise ni sa conservation ni parfois son lot
        (<RefISO code="6.6" />). Les deux sens sont donc comptés à part.
      </>}>
        <Tableau
          id="stock-prets"
          ajusterLargeur
          /* L6b (10/10/2026) : niveau STANDARD ; « Marquer rendu » quitte la
             colonne « Retour » pour la colonne d'actions (É-9) ; les prêts
             s'EXPORTENT (demande de la facturation) : export de référence. */
          niveau="standard"
          export={{ registre: 'prets', libelleRegistre: 'Prêts et emprunts', auteur: session.name || session.key }}
          lignes={vues}
          cleLigne={(p) => p.documentId}
          ligneProps={(p) => (p.archive ? { style: { opacity: 0.65 } } : {})}
          colonnes={[
            {
              cle: 'sens', libelle: 'Sens', valeur: (p) => p.sens, type: 'liste', export: true,
              libelleValeur: (v) => (v === 'entrant' ? 'Emprunté' : 'Prêté'), exporter: (p) => (p.sens === 'entrant' ? 'Emprunté' : 'Prêté'),
              style: { whiteSpace: 'nowrap' },
              rendu: (p) => (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 700,
                  padding: '3px 9px', borderRadius: 'var(--radius-pill)',
                  background: p.sens === 'entrant' ? 'var(--amber-50)' : 'var(--cyan-50)',
                  color: p.sens === 'entrant' ? 'var(--amber-700)' : 'var(--cyan-700)',
                }}>
                  <I n={p.sens === 'entrant' ? 'download' : 'send'} s={12} />
                  {p.sens === 'entrant' ? 'Emprunté' : 'Prêté'}
                </span>
              ),
            },
            {
              cle: 'date', libelle: 'Date', valeur: (p) => p.date || '', type: 'date', export: true, exporter: (p) => (p.date ? fmtDate(p.date) : ''),
              style: { ...mono, whiteSpace: 'nowrap' }, rendu: (p) => <V v={p.date ? fmtDate(p.date) : p.date} />,
            },
            {
              cle: 'article', libelle: 'Article', valeur: (p) => p.article || '', export: true,
              rendu: (p) => (
                <>
                  <V v={p.article} />
                  {p.archive ? <> <BadgeArchive motif={p.archiveMotif} /></> : null}
                </>
              ),
            },
            { cle: 'quantite', libelle: 'Qté', valeur: (p) => p.quantite || '', export: true, rendu: (p) => <V v={p.quantite} /> },
            { cle: 'laboratoire', libelle: 'Laboratoire', valeur: (p) => p.laboratoire || '', type: 'liste', export: true, rendu: (p) => <V v={p.laboratoire} /> },
            {
              /* ⚠️ La colonne porte TANTÔT un nom, TANTÔT une clé technique
                 (11/10/2026) : le formulaire enregistre le nom choisi dans la
                 liste, mais les lignes reprises de Boxylab portent la clé
                 (`coord.ezzi`). `userName` traduit la clé et laisse le nom
                 intact — appliqué aussi au TRI et à l'export, sans quoi la
                 même personne ferait deux entrées dans le filtre. */
              cle: 'personnel', libelle: 'Personnel', type: 'liste', export: true,
              valeur: (p) => (p.personnel ? userName(settings, p.personnel) : ''),
              rendu: (p) => <V v={p.personnel ? userName(settings, p.personnel) : p.personnel} />,
            },
            {
              cle: 'retour', libelle: 'Retour',
              // Trié sur le SORT du prêt et non sur le libellé du badge : ce
              // qu'on cherche en triant cette colonne, ce sont les prêts dont
              // personne n'a tranché le retour — ils remontent en premier.
              valeur: (p) => (estOui(p.rendu) ? 2 : estNon(p.rendu) ? 1 : 0),
              type: 'liste', libelleValeur: (v) => ({ 0: 'à trancher', 1: 'non rendu', 2: 'rendu' } as Record<string, string>)[v] ?? v,
              export: true, exporter: (p) => (estOui(p.rendu) ? 'rendu' : estNon(p.rendu) ? 'non rendu' : 'à trancher'),
              style: { whiteSpace: 'nowrap' },
              rendu: (p) => <BadgeRetour p={p} />,
            },
            { cle: 'boxylab', libelle: 'Boxylab', valeur: (p) => p.saisieBoxylab || '', export: true, rendu: (p) => <V v={p.saisieBoxylab} /> },
          ]}
          /* « Marquer rendu » — le geste le plus fréquent du registre — en TÊTE de
             la colonne d'actions, avant corriger/archiver. */
          actions={isRQ ? (p) => (
            <>
              {!estOui(p.rendu) && !p.archive && (
                <Bouton taille="sm" onClick={() => setRetour(p)} disabled={estOccupe(`rendu-${p.documentId}`)} title="Acter le retour — la date se confirme, elle n’est pas supposée">
                  <I n="check" s={12} />Marquer rendu
                </Bouton>
              )}
              <ActionsLigne archive={!!p.archive} onCorriger={() => setEdition(p)} onArchiver={() => setArchivage(p)} />
            </>
          ) : undefined}
        />
      </Cadre>
      {!vues.length && <EmptyState titre="Aucun prêt ne correspond à ces critères." />}
      {edition !== null && (
        <FormulaireLigne chemin="ged/stock/prets" titreCreation="Déclarer un prêt ou un emprunt"
          champs={PRETS_CHAMPS} ligne={edition === 'nouveau' ? null : edition as unknown as Record<string, unknown>}
          onClose={() => setEdition(null)} onDone={() => setVersion((n) => n + 1)} />
      )}
      {retour && (
        <RetourModale p={retour} onClose={() => setRetour(null)}
          occupe={estOccupe(`rendu-${retour.documentId}`)}
          onValider={(d, c) => acterRetour(retour, d, c)} />
      )}
      {archivage && (
        <ArchiverModale chemin="ged/stock/prets" ligne={archivage}
          refLigne={`${archivage.sens === 'entrant' ? 'Emprunt' : 'Prêt'} — ${archivage.article}${archivage.laboratoire ? ` (${archivage.laboratoire})` : ''}`}
          onClose={() => setArchivage(null)} onDone={() => setVersion((n) => n + 1)} />
      )}
    </div>
  );
}

/* ── Distribution de tubes ────────────────────────────────────────────────── */

const DIST_CHAMPS: readonly ChampDef[] = [
  { k: 'nature', label: 'Nature', type: 'options', options: [['portoir', 'Portoir (sortie courante)'], ['carton', 'Carton (sortie de réserve)']] },
  { k: 'date', label: 'Date' },
  { k: 'produitRef', label: 'Référence produit', requis: true },
  { k: 'produitLibelle', label: 'Libellé produit' },
  { k: 'quantite', label: 'Quantité' },
  { k: 'personnel', label: 'Personnel' },
  { k: 'etablissement', label: 'Établissement (abréviation)' },
  { k: 'etablissementLibelle', label: 'Établissement (libellé)' },
  { k: 'codeBoxystock', label: 'Code Boxystock' },
  { k: 'remarque', label: 'Remarque', type: 'long' },
];

function Distributions({ isRQ, signalCreation }: { isRQ: boolean; signalCreation: number }) {
  // Le registre des personnes : c'est lui qui donne un nom à `personnelCle`.
  const { settings } = useGed();
  const [lignes, setLignes] = useState<Distribution[] | null>(null);
  const [version, setVersion] = useState(0);
  const [q, setQ] = useState('');
  const [f, setF] = useState('tous');
  const [edition, setEdition] = useState<Distribution | 'nouveau' | null>(null);
  const [archivage, setArchivage] = useState<Distribution | null>(null);
  useDemandeCreation(signalCreation, () => setEdition('nouveau'));

  useEffect(() => {
    let vivant = true;
    void tout<Distribution>('ged-distribution-tubes', 'date:desc')
      .then((d) => { if (vivant) setLignes(d); });
    return () => { vivant = false; };
  }, [version]);

  const l = useMemo(() => (lignes || []).filter((d) => !d.archive), [lignes]);
  const archivees = useMemo(() => (lignes || []).filter((d) => d.archive), [lignes]);
  const portoirs = l.filter((d) => d.nature === 'portoir');
  const cartons = l.filter((d) => d.nature === 'carton');
  const vues = useMemo(() => {
    const t = q.trim().toLowerCase();
    const base = f === 'portoirs' ? portoirs : f === 'cartons' ? cartons : f === 'archives' ? archivees : l;
    return base.filter((d) => !t || [d.produitRef, d.etablissement, d.etablissementLibelle, d.personnel]
      .some((v) => (v || '').toLowerCase().includes(t)));
  }, [lignes, q, f]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!lignes) return <Skeleton rows={6} />;
  if (!lignes.length && edition === null) {
    return (
      <EmptyState
        titre="Aucune distribution de tubes n’est enregistrée."
        indice="Le tube distribué détermine l’anticoagulant, donc la recevabilité de l’échantillon qui reviendra. Sans trace, une erreur de tube ne se lit qu’un mois plus tard, en non-conformité pré-analytique." />
    );
  }

  return (
    <div>
      <FilterBar
        q={q} onQ={setQ} placeholder="Chercher un établissement, un produit…"
        chips={[
          { k: 'tous', label: 'Tous les mouvements', n: l.length },
          { k: 'portoirs', label: 'Sorties en portoirs', n: portoirs.length },
          { k: 'cartons', label: 'Sorties en cartons', n: cartons.length },
          ...(archivees.length ? [{ k: 'archives', label: 'Archivés', n: archivees.length }] : []),
        ]}
        actif={f} onChip={setF}
        total={l.length} affiches={vues.length}
        onReset={() => { setQ(''); setF('tous'); }} />
      <Cadre note={<>Portoirs et cartons ne s’additionnent pas : ce sont deux unités différentes du classeur d’origine. Le compte est donc donné séparément, jamais en total.</>}>
        <Tableau
          id="stock-distributions"
          ajusterLargeur
          /* L6b (10/10/2026) : niveau STANDARD ; les gestes RQ en colonne d'actions (É-9). */
          niveau="standard"
          lignes={vues}
          cleLigne={(d) => d.documentId}
          ligneProps={(d) => (d.archive ? { style: { opacity: 0.65 } } : {})}
          colonnes={[
            {
              cle: 'date', libelle: 'Date', valeur: (d) => d.date || '', type: 'date',
              style: { ...mono, whiteSpace: 'nowrap' }, rendu: (d) => <V v={d.date ? fmtDate(d.date) : d.date} />,
            },
            {
              cle: 'nature', libelle: 'Nature', valeur: (d) => d.nature, type: 'liste', libelleValeur: (v) => (v === 'carton' ? 'Carton' : 'Portoir'),
              style: { whiteSpace: 'nowrap' },
              rendu: (d) => (d.nature === 'carton' ? 'Carton' : 'Portoir'),
            },
            {
              cle: 'produit', libelle: 'Produit',
              valeur: (d) => d.produitLibelle || d.produitRef || '',
              rendu: (d) => (
                <span title={d.produitLibelle || undefined}>
                  <V v={d.produitLibelle || d.produitRef} />
                  {d.archive ? <> <BadgeArchive motif={d.archiveMotif} /></> : null}
                </span>
              ),
            },
            {
              cle: 'quantite', libelle: 'Quantité',
              // Le classeur porte des quantités en TEXTE (« 2 », « 2 boîtes ») :
              // on trie sur le nombre quand il s'en dégage un, sur le texte
              // sinon. Trier « 10 » avant « 2 » sur une colonne de quantités
              // serait le défaut que ce composant existe pour éviter.
              valeur: (d) => {
                const n = parseFloat(String(d.quantite || '').replace(',', '.'));
                return Number.isFinite(n) ? n : (d.quantite || '');
              },
              rendu: (d) => <V v={d.quantite} />,
            },
            {
              cle: 'etablissement', libelle: 'Établissement', type: 'liste',
              valeur: (d) => d.etablissementLibelle || d.etablissement || '',
              rendu: (d) => (
                <span title={d.etablissementLibelle || undefined}>
                  <V v={d.etablissementLibelle || d.etablissement} />
                </span>
              ),
            },
            {
              cle: 'personnel', libelle: 'Personnel',
              // Trié sur la PERSONNE quand elle est connue : « ASMA » et
              // « asma » sont la même, et un tri sur le texte écrit les
              // séparerait en deux blocs.
              valeur: (d) => (d.personnelCle ? userName(settings, d.personnelCle) : d.personnel || ''),
              rendu: (d) => (d.personnelCle ? (
                <span title={`Écrit au classeur : « ${d.personnel || '—'} »`}>
                  {userName(settings, d.personnelCle)}
                </span>
              ) : <V v={d.personnel} />),
            },
            {
              cle: 'boxystock', libelle: 'Code Boxystock', valeur: (d) => d.codeBoxystock || '',
              style: mono, rendu: (d) => <V v={d.codeBoxystock} />,
            },
          ]}
          actions={isRQ ? (d) => (
            <ActionsLigne archive={!!d.archive} onCorriger={() => setEdition(d)} onArchiver={() => setArchivage(d)} />
          ) : undefined}
        />
      </Cadre>
      {!vues.length && <EmptyState titre="Aucun mouvement ne correspond à ces critères." />}
      {edition !== null && (
        <FormulaireLigne chemin="ged/stock/distributions" titreCreation="Déclarer une distribution de tubes"
          champs={DIST_CHAMPS} ligne={edition === 'nouveau' ? null : edition as unknown as Record<string, unknown>}
          onClose={() => setEdition(null)} onDone={() => setVersion((n) => n + 1)} />
      )}
      {archivage && (
        <ArchiverModale chemin="ged/stock/distributions" ligne={archivage}
          refLigne={`${archivage.produitLibelle || archivage.produitRef || ''} → ${archivage.etablissementLibelle || archivage.etablissement || '—'}`}
          onClose={() => setArchivage(null)} onDone={() => setVersion((n) => n + 1)} />
      )}
    </div>
  );
}

/* ── Écran ────────────────────────────────────────────────────────────────── */

type Tab = 'prets' | 'distrib' | 'missions';

export function Stock({ tab: tabImpose, encastre = false }: {
  /** Imposé par l'enveloppe « Matériel » (M1, 10/10/2026). */
  tab?: Tab;
  /** Rendu DANS « Matériel » : ni titre ni segment propres. */
  encastre?: boolean;
} = {}) {
  const [tabLocal, setTab] = useState<Tab>('prets');
  const tab = tabImpose ?? tabLocal;
  const { session } = useGed();
  const isRQ = session.roles.includes('rq');
  const peutDeclarer = session.roles.length > 0;
  const [signaux, setSignaux] = useState<Record<Tab, number>>({ prets: 0, distrib: 0, missions: 0 });
  /* Les ZONES du registre des lieux alimentent le champ « périmètre » d'une
     mission : on propose ce qui existe, sans l'imposer — une mission peut
     couvrir un périmètre qui n'a pas encore de lieu déclaré. */
  const [zones, setZones] = useState<string[]>([]);
  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged-lieu-stockages?pagination[pageSize]=200&fields[0]=zone', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!vivant || !d?.data) return;
        setZones([...new Set((d.data as { zone?: string }[]).map((x) => x.zone).filter(Boolean) as string[])].sort());
      })
      .catch(() => {});
    return () => { vivant = false; };
  }, []);
  const demanderCreation = () => setSignaux((s) => ({ ...s, [tab]: s[tab] + 1 }));

  // Déclarer un prêt ou une distribution est un geste de la personne qui le fait.
  // Les missions portent leur propre bouton (« Affecter une mission ») : il
  // dépend du rôle ET de l'onglet, et le doubler en tête ferait deux entrées
  // pour un geste.
  const action = tab === 'missions'
    ? null
    : tab === 'prets'
      ? (peutDeclarer ? 'Déclarer un prêt' : null)
      : (peutDeclarer ? 'Déclarer une distribution' : null);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {encastre ? (
        action && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Bouton variante="primaire" taille="sm" onClick={demanderCreation}><I n="plus" s={13} />{action}</Bouton>
          </div>
        )
      ) : (<>
      <TitrePage eyebrow={<>Achats, stock & logistique · <RefISO code="6.6.4" prefixe="" /></>} titre={<>Stock &amp; logistique</>}
        actions={action && (
          <Bouton variante="primaire" onClick={demanderCreation}>
            <I n="plus" s={14} />{action}
          </Bouton>
        )} />
      <Segment ariaLabel="Registre" valeur={tab} onChange={setTab}
        options={[
          { valeur: 'prets' as const, libelle: 'Prêts de produits' },
          { valeur: 'distrib' as const, libelle: 'Distribution de tubes' },
          { valeur: 'missions' as const, libelle: 'Missions d’inventaire' },
        ]} />
      </>)}
      {/* Le journal du registre AFFICHÉ. Clés applicatives depuis le
          24/08/2026 (« le registre, c'est l'appli ») ; l'histoire écrite sous
          les codes des classeurs papier se lit en héritage, bout à bout. */}
      {tab === 'prets' && <div style={{ display: 'flex', justifyContent: 'flex-end' }}><BoutonJournal reference="REG-PRETS-PRODUITS" heritage="ENR.K2.05" quoi="des prêts de produits" /></div>}
      {tab === 'distrib' && <div style={{ display: 'flex', justifyContent: 'flex-end' }}><BoutonJournal reference="REG-DISTRIBUTIONS-TUBES" heritage="ENR.K2.10" quoi="des distributions de tubes" /></div>}
      {tab === 'prets' && <Prets isRQ={isRQ} signalCreation={signaux.prets} />}
      {tab === 'distrib' && <Distributions isRQ={isRQ} signalCreation={signaux.distrib} />}
      {tab === 'missions' && <MissionsInventaire isRQ={isRQ} zones={zones} />}
      {tab !== 'missions' && (
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
        Ces registres sont tenus <strong>ici et nulle part ailleurs</strong>. Les classeurs d’origine
        (ENR.K2.05, ENR.K2.10) ne sont pas versés en gestion documentaire : les tenir aux deux
        endroits donnerait deux vérités qui divergent à la première mise à jour. Une ligne ne se supprime
        jamais — elle s’archive avec motif, et chaque correction s’écrit au journal du registre.
      </p>
      )}
    </div>
  );
}
